import { useState, type ComponentPropsWithoutRef, type Ref } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

export interface SidebarWorkspaceTriggerProps extends ComponentPropsWithoutRef<'button'> {
  /** URL of the workspace logo image. */
  logoUrl?: string | null;
  /** Workspace name, used to derive initials for the fallback and the tooltip. */
  workspaceName?: string | null;
  /** React 19 ref-as-prop: forwarded to the underlying `<button>`. */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * The workspace logo button at the top of the sidebar.
 *
 * Figma specs:
 * - 40×40 image, rounded-lg (8px), padding 8px 4px
 * - When no logo: show a generic building icon placeholder
 * - Wrapped in Tooltip showing the workspace name
 *
 * `imgError` lives in an inner component keyed on `logoUrl` so the error state
 * resets automatically on URL change without a `useEffect(...)` reset (see
 * no-use-effect skill, Rule 5).
 */
export function SidebarWorkspaceTrigger({
  logoUrl,
  workspaceName,
  disabled = false,
  className,
  ref,
  ...props
}: SidebarWorkspaceTriggerProps) {
  return (
    <SidebarWorkspaceTriggerInner
      key={logoUrl ?? '__no_logo__'}
      ref={ref}
      logoUrl={logoUrl}
      workspaceName={workspaceName}
      disabled={disabled}
      className={className}
      {...props}
    />
  );
}

SidebarWorkspaceTrigger.displayName = 'SidebarWorkspaceTrigger';

function SidebarWorkspaceTriggerInner({
  logoUrl,
  workspaceName,
  disabled = false,
  className,
  ref,
  ...props
}: SidebarWorkspaceTriggerProps) {
  const { t } = useTranslation('common');

  const [imgError, setImgError] = useState(false);

  const showFallback = !logoUrl || imgError;

  const trigger = (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      className={[
        // No padding: the button is the logo's own box, so the focus ring hugs the
        // 40px square rather than a larger hit area around it. Spacing comes from
        // the SidebarHeader.
        'flex cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent p-0 outline-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:focus-ring-sidebar',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {showFallback ? (
        <span className="flex size-3xl items-center justify-center rounded-lg bg-accent text-sm font-medium text-accent-foreground">
          {workspaceName?.[0]?.toUpperCase() ?? '?'}
        </span>
      ) : (
        <img
          src={logoUrl}
          alt={t('appShell.sidebarWorkspaceTrigger.workspaceLogo')}
          className="size-3xl rounded-lg object-cover"
          onError={() => setImgError(true)}
        />
      )}
    </button>
  );

  if (!workspaceName) {
    return trigger;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="right">{workspaceName}</TooltipContent>
    </Tooltip>
  );
}

SidebarWorkspaceTriggerInner.displayName = 'SidebarWorkspaceTriggerInner';
